/**
 * Redaction applied to every Sentry event, transaction and breadcrumb of TI Admin, so that the
 * MCP authorization flow never leaks through telemetry: the landing page query handle, the
 * interaction secret fragment, completion tickets and credential-looking values. Pure and free of
 * runtime imports; it also covers the sign-in page, whose `returnUrl` carries the landing URL.
 *
 * Only plain data is rewritten. Class instances (such as an HttpErrorResponse passed to
 * console.warn) are left to the code that produces them.
 */
import type { Breadcrumb, Event } from '@sentry/angular'

const REDACTED = '[redacted]'
const MAX_DEPTH = 8

/** Query or fragment parameters of the flow, plain or percent-encoded inside a `returnUrl` value. */
const FLOW_PARAMETER = /(request|interaction|ticket)(=|%3D)[^&#\s"'%]*/gi
/**
 * Opaque MCP credentials (`tia_`, `tir_`, `tic_`, `tit_`) and Cognito-style JWTs. The leading group
 * keeps a word like `static_...` out while still matching after a percent-encoded delimiter, which
 * a lookbehind would express but not every supported browser parses.
 */
const CREDENTIAL_VALUE =
  /(^|[^A-Za-z0-9]|%[0-9A-Fa-f]{2})(?:ti[acrt]_[A-Za-z0-9_-]{16,}|eyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]*)/g

/** Keys holding a URL or a path: their fragment is dropped wholesale, whatever it contains. */
const URL_KEYS = new Set(['url', 'from', 'to', 'href', 'referer', 'referrer', 'transaction'])
const URL_KEY_SUFFIXES = ['.url', '.full']
const FRAGMENT_KEYS = new Set(['fragment', 'http.fragment', 'url.fragment'])

export function redactMcpText(text: string): string {
  return text
    .replace(
      FLOW_PARAMETER,
      (_match, name: string, equals: string) => `${name}${equals}${REDACTED}`,
    )
    .replace(CREDENTIAL_VALUE, (_match, prefix: string) => `${prefix}${REDACTED}`)
}

export function redactMcpUrl(url: string): string {
  const text = redactMcpText(url)
  const fragmentStart = text.indexOf('#')
  return fragmentStart === -1 ? text : `${text.slice(0, fragmentStart)}#${REDACTED}`
}

const isUrlKey = (key: string) =>
  URL_KEYS.has(key) || URL_KEY_SUFFIXES.some((suffix) => key.endsWith(suffix))

const redactString = (value: string, key: string): string => {
  const lowerKey = key.toLowerCase()
  if (FRAGMENT_KEYS.has(lowerKey)) {
    return value === '' ? value : REDACTED
  }
  return isUrlKey(lowerKey) ? redactMcpUrl(value) : redactMcpText(value)
}

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

const redactValue = (value: unknown, key: string, depth: number): unknown => {
  if (typeof value === 'string') {
    return redactString(value, key)
  }
  if (depth >= MAX_DEPTH) {
    return value
  }
  if (Array.isArray(value)) {
    return value.map((item) => redactValue(item, key, depth + 1))
  }
  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([entryKey, entry]) => [
        entryKey,
        redactValue(entry, entryKey, depth + 1),
      ]),
    )
  }
  return value
}

const redact = <T>(value: T, key: string): T => redactValue(value, key, 0) as T

export function sanitizeMcpBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  const sanitized = { ...breadcrumb }
  if (breadcrumb.message !== undefined) {
    sanitized.message = redact(breadcrumb.message, 'message')
  }
  if (breadcrumb.data !== undefined) {
    sanitized.data = redact(breadcrumb.data, 'data')
  }
  return sanitized
}

export function sanitizeMcpEvent<T extends Event>(event: T): T {
  const sanitized: Event = { ...event }
  if (event.transaction !== undefined) {
    sanitized.transaction = redact(event.transaction, 'transaction')
  }
  if (event.message !== undefined) {
    sanitized.message = redact(event.message, 'message')
  }
  if (event.request !== undefined) {
    sanitized.request = redact(event.request, 'request')
  }
  if (event.contexts !== undefined) {
    sanitized.contexts = redact(event.contexts, 'contexts')
  }
  if (event.extra !== undefined) {
    sanitized.extra = redact(event.extra, 'extra')
  }
  if (event.spans !== undefined) {
    sanitized.spans = redact(event.spans, 'spans')
  }
  if (event.breadcrumbs !== undefined) {
    sanitized.breadcrumbs = event.breadcrumbs.map(sanitizeMcpBreadcrumb)
  }
  if (event.exception?.values !== undefined) {
    sanitized.exception = {
      ...event.exception,
      values: event.exception.values.map((exception) =>
        exception.value === undefined
          ? exception
          : { ...exception, value: redact(exception.value, 'value') },
      ),
    }
  }
  return sanitized as T
}
